'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LoadingSpinnerIcon } from '@/components/icons/LoadingSpinnerIcon';

const LOADING_MESSAGES = [
    'Preparing your report...',
    'Capturing widgets...',
    'Rendering charts and maps...',
    'Building PDF pages...',
    'Almost there...',
    'Polishing the final details...',
    'Compiling your dashboard...',
    'Assembling the export...',
];

/** Shuffle array (Fisher-Yates) */
function shuffle<T>(arr: T[]): T[] {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export function PdfExportOverlay() {
    const [index, setIndex] = useState(0);
    const [mounted, setMounted] = useState(false);
    const order = useRef(shuffle(LOADING_MESSAGES));

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        const tick = () => {
            const delay = 4000 + Math.random() * 2000;
            timeout = setTimeout(() => {
                setIndex((prev) => {
                    const next = prev + 1;
                    if (next >= order.current.length) {
                        order.current = shuffle(LOADING_MESSAGES);
                        return 0;
                    }
                    return next;
                });
                tick();
            }, delay);
        };
        tick();
        return () => clearTimeout(timeout);
    }, []);

    if (!mounted) return null;

    const message = order.current[index];

    return createPortal(
        <div data-pdf-overlay className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
            <LoadingSpinnerIcon className="h-16 w-16" />
            <div className="mt-6 h-8 relative w-full flex justify-center">
                <AnimatePresence mode="wait">
                    <motion.p
                        key={message}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, ease: 'easeInOut' }}
                        className="absolute text-lg font-medium text-[#2D3A3F] font-roboto"
                    >
                        {message}
                    </motion.p>
                </AnimatePresence>
            </div>
        </div>,
        document.body,
    );
}
